import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function CreateQRDialog({ open, onOpenChange, onCreated }) {
  const [tab, setTab] = useState("single");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setLabel("");
    setUrl("");
    setCount(10);
    setTab("single");
  };

  const handleSingle = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/qrs", { label, destination_url: url });
      toast.success(`QR ${data.code} dibuat`);
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleBatch = async (e) => {
    e.preventDefault();
    const n = Number(count);
    if (!Number.isFinite(n) || n < 1 || n > 500) {
      toast.error("Jumlah harus 1 - 500");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/qrs/batch", { count: n });
      toast.success(`${data.created} QR kosong dibuat`);
      reset();
      onCreated?.();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="create-qr-dialog">
        <DialogHeader>
          <DialogTitle className="jscan-brand text-2xl">Buat QR</DialogTitle>
          <DialogDescription>
            Buat QR kosong (untuk produksi akrilik) atau langsung isi tujuan.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="single" data-testid="tab-single">Satu QR</TabsTrigger>
            <TabsTrigger value="batch" data-testid="tab-batch">Batch</TabsTrigger>
          </TabsList>

          <TabsContent value="single" className="mt-4">
            <form onSubmit={handleSingle} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="label">Label toko (opsional)</Label>
                <Input
                  id="label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="mis. Kopi Janji Jiwa Senopati"
                  data-testid="single-label-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">Link Google Maps review (opsional)</Label>
                <Input
                  id="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://g.page/r/..."
                  data-testid="single-url-input"
                />
                <p className="text-xs text-muted-foreground">
                  Kosongkan jika akrilik akan dicetak dulu dan diisi belakangan.
                </p>
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-sky-600 hover:bg-sky-700"
                  data-testid="submit-single-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Buat QR
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="batch" className="mt-4">
            <form onSubmit={handleBatch} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="count">Jumlah QR kosong</Label>
                <Input
                  id="count"
                  type="number"
                  min={1}
                  max={500}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                  data-testid="batch-count-input"
                />
                <p className="text-xs text-muted-foreground">
                  Setiap QR punya kode unik. Isi tujuan belakangan saat pesanan UMKM datang.
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[10, 25, 50, 100].map((n) => (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setCount(n)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 jscan-mono"
                    data-testid={`preset-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={loading}
                  className="rounded-full bg-sky-600 hover:bg-sky-700"
                  data-testid="submit-batch-button"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Buat {count} QR
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
